import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import { toast } from 'sonner';
import {
  AlertCircle,
  Bell,
  BellOff,
  CheckCircle2,
  ChevronRight,
  PanelRightClose,
  PanelRightOpen,
  Printer,
  RotateCcw,
  Settings2,
  ShieldCheck,
  Volume2,
  VolumeX,
} from 'lucide-react';
import { supabase, subscribeToMonitor } from '../lib/supabase';
import * as api from '../lib/api';
import { useAuth } from '../contexts/AuthContext';
import { canonicalPrintGroupLabel, harmonizePrintGroup, includesPrintGroup } from '../lib/printGroups';

const STORAGE_KEY_PREFIX = 'fiscalpos_monitor';
const TICKET_MIN_WIDTH = 320;
const READY_QTY_PREFIX = 'KDS_READY_QTY ';

const STAGE_META = {
  0: {
    label: 'NEU',
    textClass: 'text-sky-600',
    pillClass: 'bg-slate-100 text-slate-700 border border-slate-200',
    filterClass: 'bg-white text-slate-700 border border-slate-300',
    actionLabel: 'IN ARBEIT',
    actionClass: 'bg-sky-600 text-white hover:bg-sky-500',
  },
  1: {
    label: 'IN ARBEIT',
    textClass: 'text-orange-500',
    pillClass: 'bg-orange-100 text-[#cf2f0d] border border-orange-200',
    filterClass: 'bg-white text-[#cf2f0d] border border-[#ffb29d]',
    actionLabel: 'FERTIG',
    actionClass: 'bg-orange-500 text-white hover:bg-orange-400',
  },
  2: {
    label: 'FERTIG',
    textClass: 'text-emerald-600',
    pillClass: 'bg-emerald-100 text-emerald-700 border border-emerald-200',
    filterClass: 'bg-white text-emerald-700 border border-emerald-300',
    actionLabel: 'ARCHIVIEREN',
    actionClass: 'bg-emerald-600 text-white hover:bg-emerald-500',
  },
};

const SOURCE_META = {
  foodora: { label: 'Foodora', className: 'bg-fuchsia-600 text-white' },
  lieferando: { label: 'Lieferando', className: 'bg-orange-500 text-white' },
  wolt: { label: 'Wolt', className: 'bg-sky-500 text-white' },
  website: { label: 'Webshop', className: 'bg-indigo-600 text-white' },
  webshop: { label: 'Webshop', className: 'bg-indigo-600 text-white' },
  pos: { label: 'POS', className: 'bg-white text-slate-700 border border-slate-200' },
};

const FULFILLMENT_META = {
  'dine-in': { label: 'Tisch', className: 'bg-slate-100 text-slate-700 border border-slate-200' },
  pickup: { label: 'Abholung', className: 'bg-emerald-50 text-emerald-700 border border-emerald-200' },
  delivery: { label: 'Lieferung', className: 'bg-cyan-50 text-cyan-700 border border-cyan-200' },
};

const COURSE_META = {
  sofort: {
    label: 'Sofort',
    badgeClass: 'border-[#ff8c76] bg-[#fff4f1] text-[#cf2f0d]',
    dotClass: 'bg-[#ff3b14]',
  },
  gang1: {
    label: 'Gang 1',
    badgeClass: 'border-sky-200 bg-sky-50 text-sky-700',
    dotClass: 'bg-sky-500',
  },
  gang2: {
    label: 'Gang 2',
    badgeClass: 'border-amber-200 bg-amber-50 text-amber-700',
    dotClass: 'bg-amber-500',
  },
  gang3: {
    label: 'Gang 3',
    badgeClass: 'border-violet-200 bg-violet-50 text-violet-700',
    dotClass: 'bg-violet-500',
  },
};

const FALLBACK_COURSE_META = {
  label: 'Gang',
  badgeClass: 'border-slate-200 bg-slate-50 text-slate-700',
  dotClass: 'bg-slate-400',
};

function normalizeIdList(values) {
  return Array.from(
    new Set(
      (Array.isArray(values) ? values : [])
        .map((value) => String(value || '').trim())
        .filter(Boolean),
    ),
  );
}

function normalizeMonitorProfile(profile = {}) {
  return {
    ...profile,
    print_groups: (profile.print_groups || []).map((group) => canonicalPrintGroupLabel(group)),
    category_ids: normalizeIdList(profile.category_ids),
    menu_ids: normalizeIdList(profile.menu_ids),
  };
}

function matchesMonitorProfile(item, profile) {
  const printGroups = profile?.print_groups || [];
  const categoryIds = profile?.category_ids || [];
  const menuIds = profile?.menu_ids || [];

  if (printGroups.length && !includesPrintGroup(printGroups, item.print_group)) {
    return false;
  }

  if (categoryIds.length && !categoryIds.includes(String(item.category_id || '').trim())) {
    return false;
  }

  if (menuIds.length && !menuIds.includes(String(item.menu_id || '').trim())) {
    return false;
  }

  return true;
}

function getTimezoneCityCandidate() {
  if (typeof window === 'undefined') return '';

  const timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone || '';
  const parts = timeZone.split('/');
  const candidate = parts[parts.length - 1] || '';
  return candidate.replace(/_/g, ' ').trim();
}

async function geocodeFallbackLocation(cityName) {
  if (!cityName) return null;

  const response = await fetch(
    `https://geocoding-api.open-meteo.com/v1/search?name=${encodeURIComponent(cityName)}&count=1&language=de&format=json`,
  );

  if (!response.ok) {
    throw new Error('Geocoding fehlgeschlagen');
  }

  const payload = await response.json();
  const first = payload?.results?.[0];
  if (!first) return null;

  return {
    latitude: first.latitude,
    longitude: first.longitude,
    label: first.name || cityName,
  };
}

function getWeatherLabel(code, isDay) {
  switch (code) {
    case 0:
      return isDay ? 'Sonnig' : 'Klar';
    case 1:
    case 2:
      return 'Leicht bewölkt';
    case 3:
      return 'Bewölkt';
    case 45:
    case 48:
      return 'Nebelig';
    case 51:
    case 53:
    case 55:
      return 'Nieselregen';
    case 56:
    case 57:
      return 'Gefrierender Nieselregen';
    case 61:
    case 63:
    case 65:
      return 'Regen';
    case 66:
    case 67:
      return 'Gefrierender Regen';
    case 71:
    case 73:
    case 75:
    case 77:
      return 'Schnee';
    case 80:
    case 81:
    case 82:
      return 'Regenschauer';
    case 85:
    case 86:
      return 'Schneeschauer';
    case 95:
      return 'Gewitter';
    case 96:
    case 99:
      return 'Gewitter mit Hagel';
    default:
      return 'Wetter';
  }
}

async function fetchCurrentWeather(latitude, longitude) {
  const response = await fetch(
    `https://api.open-meteo.com/v1/forecast?latitude=${latitude}&longitude=${longitude}&current=temperature_2m,weather_code,is_day&timezone=auto&forecast_days=1`,
  );

  if (!response.ok) {
    throw new Error('Wetter konnte nicht geladen werden');
  }

  const payload = await response.json();
  const current = payload?.current;
  if (!current) {
    throw new Error('Keine Wetterdaten vorhanden');
  }

  return {
    temperature: Math.round(current.temperature_2m),
    label: getWeatherLabel(current.weather_code, Boolean(current.is_day)),
  };
}

function getStorageKey(profileId, suffix) {
  return `${STORAGE_KEY_PREFIX}_${profileId}_${suffix}`;
}

function readStoredToggle(profileId, suffix, fallback) {
  if (typeof window === 'undefined') return fallback;

  const stored = window.localStorage.getItem(getStorageKey(profileId, suffix));
  if (stored === null) return fallback;
  return stored === '1';
}

function formatClock(timestamp) {
  if (!timestamp) return '--:--';
  return new Date(timestamp).toLocaleTimeString('de-AT', {
    hour: '2-digit',
    minute: '2-digit',
  });
}

function minutesSince(timestamp, nowTs) {
  if (!timestamp) return 0;
  return Math.max(0, Math.floor((nowTs - new Date(timestamp).getTime()) / 60000));
}

function addMinutes(timestamp, minutes) {
  if (!timestamp) return null;
  return new Date(new Date(timestamp).getTime() + minutes * 60000).toISOString();
}

function parseTicketNumber(orderNumber) {
  if (!orderNumber) return null;

  const parts = String(orderNumber).split('-');
  const candidate = parts[parts.length - 1];
  const parsed = Number.parseInt(candidate, 10);
  if (Number.isFinite(parsed)) return parsed;

  const digits = String(orderNumber).match(/\d+/g);
  if (!digits?.length) return null;
  const fallback = Number.parseInt(digits[digits.length - 1], 10);
  return Number.isFinite(fallback) ? fallback : null;
}

function formatTableLabel(tableName) {
  const normalized = String(tableName || '').trim();
  if (!normalized) return '';
  if (/^(tisch|table)\b/i.test(normalized)) return normalized;
  return `Tisch ${normalized}`;
}

function normalizeSourceKey(source) {
  const normalized = String(source || 'pos').trim().toLowerCase().replace(/[\s-]+/g, '_');
  if (!normalized) return 'pos';
  if (['website', 'webshop', 'online', 'onlineshop', 'online_shop'].includes(normalized)) {
    return 'website';
  }
  return normalized;
}

function looksLikeWebshopCashier(value) {
  return ['webshop', 'website', 'online', 'online shop', 'onlineshop'].includes(
    String(value || '').trim().toLowerCase(),
  );
}

function getEffectiveOrderSource(ticket) {
  const source = normalizeSourceKey(ticket?.orderSource);
  if (source !== 'website') return source;

  // Handle legacy Directverkauf data until its source is corrected on the server.
  if (ticket?.cashierName && !looksLikeWebshopCashier(ticket.cashierName)) {
    return 'pos';
  }

  return source;
}

function normalizeFulfillmentKey(type) {
  const normalized = String(type || '').trim().toLowerCase().replace(/_/g, '-');
  if (['delivery', 'lieferung'].includes(normalized)) return 'delivery';
  if (['pickup', 'takeaway', 'take-away', 'abholung'].includes(normalized)) return 'pickup';
  return 'dine-in';
}

function normalizeCourseKey(course) {
  const normalized = String(course || 'Sofort').trim().toLowerCase().replace(/\s+/g, '');
  if (!normalized || normalized === 'sofort' || normalized === 'immediately') return 'sofort';

  const numberedCourseMatch = normalized.match(/^(?:gang|course)(\d+)$/);
  if (numberedCourseMatch) {
    return `gang${numberedCourseMatch[1]}`;
  }

  return normalized;
}

function getCourseSortValue(courseKey) {
  if (courseKey === 'sofort') return 0;

  const numberedCourseMatch = String(courseKey || '').match(/^gang(\d+)$/);
  if (numberedCourseMatch) {
    return Number(numberedCourseMatch[1]) || 99;
  }

  return 99;
}

function getCourseMeta(course) {
  const label = String(course || 'Sofort').trim() || 'Sofort';
  const key = normalizeCourseKey(label);
  const preset = COURSE_META[key];

  return {
    key,
    label,
    ...(preset || FALLBACK_COURSE_META),
  };
}

function isPickupFulfillment(type) {
  return normalizeFulfillmentKey(type) === 'pickup';
}

function isDeliveryFulfillment(type) {
  return normalizeFulfillmentKey(type) === 'delivery';
}

function splitNoteSegments(notes) {
  return String(notes || '')
    .split('|')
    .map((segment) => segment.trim())
    .filter(Boolean);
}

function extractVisibleItemNote(notes) {
  return splitNoteSegments(notes)
    .filter((segment) => !segment.startsWith(READY_QTY_PREFIX))
    .join(' | ');
}

function getItemReadyQuantity(item) {
  const quantity = Math.max(1, Number(item?.quantity) || 1);
  const readySegment = splitNoteSegments(item?.notes).find((segment) => segment.startsWith(READY_QTY_PREFIX));

  if (readySegment) {
    const parsed = Number.parseInt(readySegment.slice(READY_QTY_PREFIX.length).trim(), 10);
    if (Number.isFinite(parsed)) {
      return Math.max(0, Math.min(quantity, parsed));
    }
  }

  return String(item?.status || '').toLowerCase() === 'ready' ? quantity : 0;
}

function setItemReadyQuantity(notes, readyQuantity) {
  const segments = splitNoteSegments(notes).filter((segment) => !segment.startsWith(READY_QTY_PREFIX));
  const safeReadyQuantity = Math.max(0, Number(readyQuantity) || 0);
  if (safeReadyQuantity > 0) {
    segments.push(`${READY_QTY_PREFIX}${safeReadyQuantity}`);
  }
  return segments.join(' | ');
}

function buildItemWithReadyQuantity(item, readyQuantity) {
  const quantity = Math.max(1, Number(item?.quantity) || 1);
  const nextReadyQuantity = Math.max(0, Math.min(quantity, Number(readyQuantity) || 0));

  return {
    ...item,
    status: nextReadyQuantity >= quantity ? 'ready' : 'pending',
    notes: setItemReadyQuantity(item?.notes, nextReadyQuantity),
  };
}

function getSourceMeta(source) {
  const normalized = normalizeSourceKey(source);
  return SOURCE_META[normalized] || SOURCE_META.pos;
}

function getFulfillmentMeta(ticket) {
  const key = normalizeFulfillmentKey(ticket?.fulfillmentType);
  if (key === 'dine-in' && !ticket?.tableName) {
    return {
      ...FULFILLMENT_META['dine-in'],
      label: 'Vor Ort',
    };
  }
  return FULFILLMENT_META[key] || FULFILLMENT_META['dine-in'];
}

function isPosTicket(ticket) {
  return getEffectiveOrderSource(ticket) === 'pos';
}

function getTicketBadge(ticket) {
  return getSourceMeta(getEffectiveOrderSource(ticket));
}

function getTicketTitle(ticket) {
  if (isPosTicket(ticket)) {
    if (ticket.tableName) return formatTableLabel(ticket.tableName);
    if (ticket.customerName) return ticket.customerName;
    return 'Direktverkauf';
  }

  if (isDeliveryFulfillment(ticket.fulfillmentType)) return 'Lieferung';
  return 'Abholung';
}

function getArrivalTimestamp(ticket) {
  return ticket.orderCreatedAt || ticket.created_at;
}

function getDueMeta(ticket, orderTimes) {
  const arrivalTimestamp = getArrivalTimestamp(ticket);
  const explicitTimestamp = ticket.confirmedFulfillmentAt || ticket.requestedFulfillmentAt || null;
  if (!arrivalTimestamp && !explicitTimestamp) return { label: '', timestamp: null };

  if (isDeliveryFulfillment(ticket.fulfillmentType)) {
    return {
      label: 'Lieferung',
      timestamp: explicitTimestamp || addMinutes(arrivalTimestamp, orderTimes.deliveryLeadMinutes || 35),
    };
  }

  if (!isPosTicket(ticket) || isPickupFulfillment(ticket.fulfillmentType)) {
    return {
      label: 'Abholung',
      timestamp: explicitTimestamp || addMinutes(arrivalTimestamp, orderTimes.pickupLeadMinutes || 20),
    };
  }

  return { label: '', timestamp: null };
}

function getTimerTone(minutes, warnMinutes, alertMinutes) {
  if (minutes >= alertMinutes) return 'text-red-500';
  if (minutes >= warnMinutes) return 'text-amber-500';
  return 'text-slate-500';
}

function normalizeModifierLabel(modifier) {
  return String(modifier?.name || modifier || '').trim();
}

function getItemGroupKey(item) {
  const modifiers = (item.modifiers || []).map(normalizeModifierLabel).join('|');
  const notes = extractVisibleItemNote(item.notes);
  return [
    normalizeCourseKey(item.course),
    String(item.product_name || '').trim(),
    notes,
    modifiers,
  ].join('::');
}

function buildTicketItems(items) {
  return items
    .slice()
    .sort((left, right) => {
      const leftCourseOrder = getCourseSortValue(normalizeCourseKey(left.course));
      const rightCourseOrder = getCourseSortValue(normalizeCourseKey(right.course));
      if (leftCourseOrder !== rightCourseOrder) {
        return leftCourseOrder - rightCourseOrder;
      }

      const leftCreated = left.created_at ? new Date(left.created_at).getTime() : 0;
      const rightCreated = right.created_at ? new Date(right.created_at).getTime() : 0;

      if (leftCreated !== rightCreated) {
        return leftCreated - rightCreated;
      }

      return String(left.product_name || '').localeCompare(String(right.product_name || ''));
    });
}

function buildDisplayItems(items) {
  const grouped = new Map();

  buildTicketItems(items).forEach((item) => {
    const groupKey = getItemGroupKey(item);
    const quantity = Math.max(1, Number(item.quantity) || 1);
    const courseMeta = getCourseMeta(item.course);
    const current = grouped.get(groupKey) || {
      key: groupKey,
      items: [],
      product_name: item.product_name,
      modifiers: item.modifiers || [],
      notes: extractVisibleItemNote(item.notes),
      quantity: 0,
      readyQuantity: 0,
      createdAt: item.created_at || item.order_created_at || null,
      course: courseMeta.label,
      courseKey: courseMeta.key,
      courseMeta,
    };

    current.items.push(item);
    current.quantity += quantity;
    current.readyQuantity += getItemReadyQuantity(item);

    if (!current.createdAt || new Date(item.created_at || item.order_created_at || 0) < new Date(current.createdAt)) {
      current.createdAt = item.created_at || item.order_created_at || current.createdAt;
    }

    grouped.set(groupKey, current);
  });

  return Array.from(grouped.values())
    .map((group) => ({
      ...group,
      allReady: group.readyQuantity >= group.quantity,
      partiallyReady: group.readyQuantity > 0 && group.readyQuantity < group.quantity,
    }))
    .sort((left, right) => {
      const leftCourseOrder = getCourseSortValue(left.courseKey);
      const rightCourseOrder = getCourseSortValue(right.courseKey);
      if (leftCourseOrder !== rightCourseOrder) {
        return leftCourseOrder - rightCourseOrder;
      }

      const leftCreated = left.createdAt ? new Date(left.createdAt).getTime() : 0;
      const rightCreated = right.createdAt ? new Date(right.createdAt).getTime() : 0;

      if (leftCreated !== rightCreated) {
        return leftCreated - rightCreated;
      }

      return String(left.product_name || '').localeCompare(String(right.product_name || ''));
    });
}

function buildCourseSections(displayItems) {
  const grouped = new Map();

  displayItems.forEach((itemGroup) => {
    const sectionKey = itemGroup.courseKey || normalizeCourseKey(itemGroup.course);
    const sectionMeta = itemGroup.courseMeta || getCourseMeta(itemGroup.course);
    const current = grouped.get(sectionKey) || {
      key: sectionKey,
      meta: sectionMeta,
      items: [],
      totalQuantity: 0,
      readyQuantity: 0,
    };

    current.items.push(itemGroup);
    current.totalQuantity += itemGroup.quantity;
    current.readyQuantity += itemGroup.readyQuantity;
    grouped.set(sectionKey, current);
  });

  return Array.from(grouped.values())
    .map((section) => ({
      ...section,
      remainingQuantity: Math.max(0, section.totalQuantity - section.readyQuantity),
    }))
    .sort((left, right) => {
      const leftOrder = getCourseSortValue(left.key);
      const rightOrder = getCourseSortValue(right.key);
      if (leftOrder !== rightOrder) {
        return leftOrder - rightOrder;
      }

      return String(left.meta?.label || '').localeCompare(String(right.meta?.label || ''));
    });
}

function buildTicketFromItems(items) {
  if (!items.length) return null;

  const orderedItems = items.slice().sort((left, right) => new Date(left.created_at) - new Date(right.created_at));
  const first = orderedItems[0];
  const stepValues = orderedItems.map((item) => item.monitor_step).filter((step) => step >= 0);
  const uniqueSteps = Array.from(new Set(stepValues));

  return {
    key: first.order_id || first.order_number || first.id,
    orderId: first.order_id,
    orderNumber: first.order_number,
    tableName: first.table_name,
    roomName: first.room_name,
    cashierName: first.cashier_name,
    customerName: first.customer_name,
    fulfillmentType: first.fulfillment_type,
    orderSource: first.order_source,
    requestedFulfillmentAt: first.requested_fulfillment_at,
    confirmedFulfillmentAt: first.confirmed_fulfillment_at,
    created_at: first.created_at,
    orderCreatedAt: first.order_created_at || first.created_at,
    items: orderedItems,
    stepValues: uniqueSteps,
    stationGroups: Array.from(new Set(orderedItems.map((item) => item.print_group).filter(Boolean))),
    activeStep: uniqueSteps.length ? Math.min(...uniqueSteps) : 0,
  };
}

function playKitchenAlertTone() {
  if (typeof window === 'undefined') return;
  const AudioContextClass = window.AudioContext || window.webkitAudioContext;
  if (!AudioContextClass) return;

  try {
    const context = new AudioContextClass();
    const oscillator = context.createOscillator();
    const gainNode = context.createGain();

    oscillator.type = 'square';
    oscillator.frequency.setValueAtTime(880, context.currentTime);
    oscillator.connect(gainNode);
    gainNode.connect(context.destination);

    gainNode.gain.setValueAtTime(0.0001, context.currentTime);
    gainNode.gain.exponentialRampToValueAtTime(0.08, context.currentTime + 0.02);
    gainNode.gain.exponentialRampToValueAtTime(0.0001, context.currentTime + 0.23);

    oscillator.start(context.currentTime);
    oscillator.stop(context.currentTime + 0.24);

    oscillator.onended = () => {
      context.close().catch(() => {});
    };
  } catch {
    // Ignore autoplay restrictions.
  }
}

function speakTicketAnnouncement(ticket) {
  if (typeof window === 'undefined' || !window.speechSynthesis || !ticket) return;

  try {
    const badge = getTicketBadge(ticket);
    const title = getTicketTitle(ticket);
    const spokenNumber = parseTicketNumber(ticket.orderNumber) || ticket.orderNumber || '';
    const utterance = new SpeechSynthesisUtterance(`Neue Bestellung ${spokenNumber}. ${badge.label}. ${title}.`);
    utterance.lang = 'de-DE';
    utterance.rate = 1;
    window.speechSynthesis.cancel();
    window.speechSynthesis.speak(utterance);
  } catch {
    // Ignore speech synthesis failures.
  }
}

function MonitorTicket({
  ticket,
  ticketIndex,
  nowTs,
  warnMinutes,
  alertMinutes,
  orderTimes,
  showOrderSource,
  showFulfillmentType,
  isPrinting,
  onAdvanceTicket,
  onDoneTicket,
  onMarkItemGroupPortionReady,
  onPrintTicket,
}) {
  const stageMeta = STAGE_META[ticket.activeStep] || STAGE_META[0];
  const sourceBadge = getTicketBadge(ticket);
  const fulfillmentBadge = getFulfillmentMeta(ticket);
  const arrivalTimestamp = getArrivalTimestamp(ticket);
  const dueMeta = getDueMeta(ticket, orderTimes);
  const ageMinutes = minutesSince(arrivalTimestamp, nowTs);
  const timerClass = getTimerTone(ageMinutes, warnMinutes, alertMinutes);
  const displayNumber = parseTicketNumber(ticket.orderNumber) || (ticketIndex + 1);
  const displayItems = buildDisplayItems(ticket.items);
  const courseSections = buildCourseSections(displayItems);
  const nextCourse = courseSections.find((section) => section.remainingQuantity > 0) || courseSections[0] || null;
  const showNextCourse = nextCourse && (courseSections.length > 1 || nextCourse.key !== 'sofort');
  const actionLabel = ticket.activeStep >= 2 ? STAGE_META[2].actionLabel : stageMeta.actionLabel;

  return (
    <article className="flex w-full flex-col border border-slate-200 bg-white shadow-[0_10px_24px_rgba(15,23,42,0.06)]">
      <div className="flex items-start justify-between gap-2 px-3 pt-3">
        <div className="min-w-0 flex-1">
          <div className="flex items-baseline gap-2">
            <span className="text-[18px] font-black leading-none text-[#ff3b14]">
              #{displayNumber}
            </span>
            <p className="truncate text-[17px] font-black leading-none text-slate-900">
              {getTicketTitle(ticket)}
            </p>
          </div>
        </div>

        <div className="flex shrink-0 flex-wrap justify-end gap-1">
          <button
            type="button"
            onClick={() => onPrintTicket(ticket)}
            disabled={isPrinting}
            title="Produktionsbon drucken"
            aria-label={`Bestellung ${displayNumber} drucken`}
            className="inline-flex h-7 w-7 items-center justify-center text-slate-700 transition-colors hover:bg-slate-100 hover:text-slate-950 disabled:cursor-wait disabled:opacity-60"
          >
            <Printer className="h-3.5 w-3.5" />
          </button>
          {showFulfillmentType ? (
            <span className={`inline-flex items-center rounded-full px-2 py-1 text-[10px] font-black tracking-[0.08em] ${fulfillmentBadge.className}`}>
              {fulfillmentBadge.label}
            </span>
          ) : null}
          {showOrderSource ? (
            <span className={`inline-flex items-center rounded-full px-2 py-1 text-[10px] font-black tracking-[0.08em] ${sourceBadge.className}`}>
              {sourceBadge.label}
            </span>
          ) : null}
        </div>
      </div>

      <div className="flex items-start justify-between gap-2 px-3 pt-2">
        <div className="min-w-0 space-y-1">
          <div className="flex flex-wrap items-center gap-1.5">
            <span className={`inline-flex items-center rounded-full px-2 py-1 text-[10px] font-black tracking-[0.08em] ${stageMeta.pillClass}`}>
              {stageMeta.label}
            </span>
            {showNextCourse ? (
              <span className={`inline-flex items-center gap-1 rounded-full border px-2 py-1 text-[10px] font-black tracking-[0.08em] ${nextCourse.meta.badgeClass}`}>
                <span className={`h-1.5 w-1.5 rounded-full ${nextCourse.meta.dotClass}`} />
                {nextCourse.meta.label} zuerst
              </span>
            ) : null}
          </div>
          {ticket.stationGroups.length ? (
            <p className="truncate text-[10px] font-semibold uppercase tracking-[0.14em] text-slate-400">
              {ticket.stationGroups.join(' / ')}
            </p>
          ) : null}
        </div>

        <div className="shrink-0 text-right">
          {dueMeta.timestamp ? (
            <p className="text-[11px] font-black text-[#cf2f0d]">
              {dueMeta.label} {formatClock(dueMeta.timestamp)}
            </p>
          ) : null}
          <p className={`mt-0.5 text-[13px] font-bold ${timerClass}`}>
            {ageMinutes}m
          </p>
        </div>
      </div>

      {(ticket.customerName || ticket.cashierName || ticket.roomName || arrivalTimestamp) ? (
        <div className="flex flex-wrap items-center justify-between gap-2 px-3 pt-2 text-[11px] font-medium text-slate-500">
          <span className="truncate">
            {ticket.customerName || ticket.cashierName || ticket.roomName || ''}
          </span>
          {arrivalTimestamp ? (
            <span className="shrink-0">seit {formatClock(arrivalTimestamp)}</span>
          ) : null}
        </div>
      ) : null}

      <div className="mt-2 border-t border-[#ff8c76]">
        {courseSections.map((section) => (
          <div key={section.key} className="border-b border-slate-200/80 last:border-b-0">
            <div className="flex items-center justify-between gap-2 bg-slate-50 px-3 py-1.5">
              <span className={`inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[10px] font-black tracking-[0.08em] ${section.meta.badgeClass}`}>
                <span className={`h-1.5 w-1.5 rounded-full ${section.meta.dotClass}`} />
                {section.meta.label}
              </span>
              <span className="text-[10px] font-bold uppercase tracking-[0.08em] text-slate-500">
                {section.remainingQuantity > 0 ? `${section.remainingQuantity} offen` : 'Fertig'}
              </span>
            </div>

            <div className="space-y-0 px-3 py-1">
              {section.items.map((itemGroup) => {
                const ready = itemGroup.allReady;

                return (
                  <button
                    key={itemGroup.key}
                    type="button"
                    onClick={() => onMarkItemGroupPortionReady(itemGroup.items)}
                    className={`block w-full border-b border-dashed border-slate-200 px-0 py-2 text-left transition-colors last:border-b-0 ${
                      ready
                        ? 'bg-[#e5f8e8]'
                        : itemGroup.partiallyReady
                          ? 'bg-[#eff9f0]'
                          : 'bg-transparent hover:bg-slate-50'
                    }`}
                  >
                    <div className="space-y-1">
                      <div className="flex items-start justify-between gap-2">
                        <p className={`text-[14px] font-bold leading-tight ${ready ? 'text-emerald-800' : itemGroup.partiallyReady ? 'text-emerald-700' : 'text-slate-900'}`}>
                          {itemGroup.quantity}x {itemGroup.product_name}
                        </p>
                        {ready ? (
                          <span className="shrink-0 rounded-full bg-emerald-600 px-2 py-0.5 text-[10px] font-black uppercase tracking-[0.08em] text-white">
                            OK
                          </span>
                        ) : itemGroup.partiallyReady ? (
                          <span className="shrink-0 rounded-full bg-emerald-500 px-2 py-0.5 text-[10px] font-black uppercase tracking-[0.08em] text-white">
                            {itemGroup.readyQuantity}/{itemGroup.quantity}
                          </span>
                        ) : null}
                      </div>
                      {(itemGroup.modifiers || []).map((modifier, index) => (
                        <p key={`${itemGroup.key}-modifier-${index}`} className={`pl-3 text-[11px] ${ready ? 'text-emerald-700/80' : itemGroup.partiallyReady ? 'text-emerald-700/80' : 'text-slate-500'}`}>
                          + {modifier.name || modifier}
                        </p>
                      ))}
                      {itemGroup.notes ? (
                        <p className={`pl-3 text-[11px] ${ready ? 'text-emerald-700/80' : itemGroup.partiallyReady ? 'text-emerald-700/80' : 'text-slate-500'}`}>
                          + {itemGroup.notes}
                        </p>
                      ) : null}
                    </div>
                  </button>
                );
              })}
            </div>
          </div>
        ))}
      </div>

      <button
        type="button"
        onClick={() => (
          ticket.activeStep >= 2
            ? onDoneTicket(ticket)
            : onAdvanceTicket(ticket, ticket.activeStep + 1)
        )}
        className={`inline-flex items-center justify-between gap-3 px-3 py-2.5 text-left text-[11px] font-black uppercase tracking-[0.08em] transition-colors ${ticket.activeStep >= 2 ? STAGE_META[2].actionClass : stageMeta.actionClass}`}
      >
        <span>{actionLabel}</span>
        <ChevronRight className="h-4 w-4" />
      </button>
    </article>
  );
}

function OpenItemsRail({ tickets, onMarkOpenItemReady }) {
  const rows = useMemo(() => {
    const grouped = new Map();

    tickets.forEach((ticket) => {
      ticket.items.forEach((item) => {
        const remainingQuantity = Math.max(0, (Number(item.quantity) || 1) - getItemReadyQuantity(item));
        if (remainingQuantity <= 0) return;

        const productLabel = item.product_name || 'Artikel';
        const courseMeta = getCourseMeta(item.course);
        const rowKey = `${courseMeta.key}::${productLabel}`;
        const current = grouped.get(rowKey) || {
          key: rowKey,
          label: productLabel,
          courseMeta,
          quantity: 0,
          items: [],
        };
        current.quantity += remainingQuantity;
        current.items.push(item);
        grouped.set(rowKey, current);
      });
    });

    return Array.from(grouped.values())
      .sort((left, right) => {
        const leftCourseOrder = getCourseSortValue(left.courseMeta?.key);
        const rightCourseOrder = getCourseSortValue(right.courseMeta?.key);
        if (leftCourseOrder !== rightCourseOrder) return leftCourseOrder - rightCourseOrder;
        if (right.quantity !== left.quantity) return right.quantity - left.quantity;
        return left.label.localeCompare(right.label);
      });
  }, [tickets]);

  return (
    <aside className="flex min-h-0 flex-col border-l border-slate-800 bg-[#101827]">
      <div className="border-b border-slate-800 px-4 py-4">
        <p className="text-[11px] font-black uppercase tracking-[0.12em] text-slate-400">
          OFFENE ARTIKEL
        </p>
      </div>

      <div className="min-h-0 flex-1 overflow-y-auto">
        {rows.length ? (
          rows.map((row) => (
            <button
              key={row.key}
              type="button"
              onClick={() => onMarkOpenItemReady(row.items)}
              className="flex w-full items-center justify-between gap-3 border-b border-slate-800 px-4 py-3 text-[13px] transition-colors hover:bg-slate-800/80"
            >
              <div className="min-w-0">
                <span className="truncate text-slate-100">{row.label}</span>
                {row.courseMeta.key !== 'sofort' ? (
                  <div>
                    <span className={`mt-1 inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[10px] font-black uppercase tracking-[0.08em] ${row.courseMeta.badgeClass}`}>
                      <span className={`h-1.5 w-1.5 rounded-full ${row.courseMeta.dotClass}`} />
                      {row.courseMeta.label}
                    </span>
                  </div>
                ) : null}
              </div>
              <span className="font-black text-white">{row.quantity}x</span>
            </button>
          ))
        ) : (
          <div className="px-4 py-8 text-sm text-slate-500">
            Keine offenen Artikel.
          </div>
        )}
      </div>
    </aside>
  );
}

export default function KitchenTableMonitor() {
  const { profileId } = useParams();
  const { isAuthenticated, isLoading: authLoading } = useAuth();

  const channelRef = useRef(null);
  const reloadTimeoutRef = useRef(null);
  const undoTimeoutRef = useRef(null);
  const boardRef = useRef(null);
  const itemsRef = useRef([]);
  const soundEnabledRef = useRef(true);
  const voiceEnabledRef = useRef(false);

  const [profile, setProfile] = useState(null);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [nowTs, setNowTs] = useState(Date.now());
  const [selectedStation, setSelectedStation] = useState('all');
  const [selectedStage, setSelectedStage] = useState('open');
  const [showOnlyAlerts, setShowOnlyAlerts] = useState(false);
  const [showSummary, setShowSummary] = useState(true);
  const [ticketColumnLimit, setTicketColumnLimit] = useState(4);
  const [showMonitorSettings, setShowMonitorSettings] = useState(false);
  const [monitorSettingsPin, setMonitorSettingsPin] = useState('');
  const [monitorSettingsUnlockedUntil, setMonitorSettingsUnlockedUntil] = useState(0);
  const [unlockingMonitorSettings, setUnlockingMonitorSettings] = useState(false);
  const [soundEnabled, setSoundEnabled] = useState(true);
  const [voiceEnabled, setVoiceEnabled] = useState(false);
  const [printingTicketIds, setPrintingTicketIds] = useState([]);
  const [undoAction, setUndoAction] = useState(null);
  const [boardWidth, setBoardWidth] = useState(0);
  const [weather, setWeather] = useState({
    status: 'loading',
    temperature: null,
    label: 'Wetter wird geladen',
    location: '',
  });

  useEffect(() => {
    itemsRef.current = items;
  }, [items]);

  useEffect(() => {
    soundEnabledRef.current = soundEnabled;
  }, [soundEnabled]);

  useEffect(() => {
    voiceEnabledRef.current = voiceEnabled;
  }, [voiceEnabled]);

  const clearUndoAction = useCallback(() => {
    if (undoTimeoutRef.current) {
      window.clearTimeout(undoTimeoutRef.current);
      undoTimeoutRef.current = null;
    }
    setUndoAction(null);
  }, []);

  const queueUndoAction = useCallback((action) => {
    if (undoTimeoutRef.current) {
      window.clearTimeout(undoTimeoutRef.current);
    }

    setUndoAction(action);
    undoTimeoutRef.current = window.setTimeout(() => {
      setUndoAction(null);
      undoTimeoutRef.current = null;
    }, 8000);
  }, []);

  useEffect(() => {
    const timer = window.setInterval(() => setNowTs(Date.now()), 30000);
    return () => window.clearInterval(timer);
  }, []);

  useEffect(() => {
    if (typeof window === 'undefined' || !boardRef.current) return undefined;

    const updateBoardWidth = () => {
      setBoardWidth(boardRef.current?.getBoundingClientRect().width || 0);
    };

    updateBoardWidth();

    if (typeof window.ResizeObserver === 'undefined') {
      window.addEventListener('resize', updateBoardWidth);
      return () => window.removeEventListener('resize', updateBoardWidth);
    }

    const observer = new window.ResizeObserver(() => {
      updateBoardWidth();
    });
    observer.observe(boardRef.current);

    return () => observer.disconnect();
  }, [showSummary, items.length, selectedStage, selectedStation, showOnlyAlerts]);

  useEffect(() => {
    let cancelled = false;
    let refreshTimer = null;

    const setWeatherState = (nextState) => {
      if (!cancelled) {
        setWeather(nextState);
      }
    };

    const loadWeatherFromCoordinates = async (latitude, longitude, location) => {
      try {
        const currentWeather = await fetchCurrentWeather(latitude, longitude);
        setWeatherState({
          status: 'ready',
          temperature: currentWeather.temperature,
          label: currentWeather.label,
          location: location || '',
        });
      } catch (error) {
        console.error(error);
        setWeatherState({
          status: 'error',
          temperature: null,
          label: 'Wetter nicht verfügbar',
          location: '',
        });
      }
    };

    const loadFallbackWeather = async () => {
      try {
        const cityCandidate = getTimezoneCityCandidate();
        const fallbackLocation = await geocodeFallbackLocation(cityCandidate);

        if (!fallbackLocation) {
          setWeatherState({
            status: 'error',
            temperature: null,
            label: 'Wetter nicht verfügbar',
            location: '',
          });
          return;
        }

        await loadWeatherFromCoordinates(
          fallbackLocation.latitude,
          fallbackLocation.longitude,
          fallbackLocation.label,
        );
      } catch (error) {
        console.error(error);
        setWeatherState({
          status: 'error',
          temperature: null,
          label: 'Wetter nicht verfügbar',
          location: '',
        });
      }
    };

    const refreshWeather = async () => {
      if (typeof window === 'undefined' || !navigator.geolocation) {
        await loadFallbackWeather();
        return;
      }

      navigator.geolocation.getCurrentPosition(
        async (position) => {
          await loadWeatherFromCoordinates(
            position.coords.latitude,
            position.coords.longitude,
            '',
          );
        },
        async () => {
          await loadFallbackWeather();
        },
        {
          enableHighAccuracy: false,
          timeout: 10000,
          maximumAge: 10 * 60 * 1000,
        },
      );
    };

    refreshWeather();
    refreshTimer = window.setInterval(refreshWeather, 10 * 60 * 1000);

    return () => {
      cancelled = true;
      if (refreshTimer) {
        window.clearInterval(refreshTimer);
      }
    };
  }, []);

  useEffect(() => {
    if (!authLoading && !isAuthenticated) {
      window.location.replace(`/pos/login?redirect=${encodeURIComponent(window.location.pathname)}`);
    }
  }, [authLoading, isAuthenticated]);

  useEffect(() => {
    setVoiceEnabled(readStoredToggle(profileId, 'voice', false));
    setSoundEnabled(readStoredToggle(profileId, 'sound', true));
    const storedColumns = Number.parseInt(window.localStorage.getItem(getStorageKey(profileId, 'ticket_columns')), 10);
    const defaultColumns = window.innerWidth >= 1920 ? 5 : 4;
    setTicketColumnLimit(storedColumns >= 2 && storedColumns <= 6 ? storedColumns : defaultColumns);
  }, [profileId]);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    window.localStorage.setItem(getStorageKey(profileId, 'voice'), voiceEnabled ? '1' : '0');
  }, [profileId, voiceEnabled]);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    window.localStorage.setItem(getStorageKey(profileId, 'sound'), soundEnabled ? '1' : '0');
  }, [profileId, soundEnabled]);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    window.localStorage.setItem(getStorageKey(profileId, 'ticket_columns'), String(ticketColumnLimit));
  }, [profileId, ticketColumnLimit]);

  const loadMonitorData = useCallback(async ({ silent = false } = {}) => {
    if (authLoading || !isAuthenticated) {
      return;
    }

    if (!silent) setLoading(true);
    try {
      const loadedProfile = await api.getMonitorProfile(profileId);
      const normalizedProfile = normalizeMonitorProfile(loadedProfile);
      setProfile(normalizedProfile);

      const activeItems = await api.getMonitorOrders(normalizedProfile.company_id || null, null);
      const normalizedItems = activeItems.map((item) => ({
        ...item,
        print_group: harmonizePrintGroup(item.print_group, normalizedProfile.print_groups),
      }));
      const filteredItems = normalizedItems.filter((item) => matchesMonitorProfile(item, normalizedProfile));

      setItems(filteredItems.filter((item) => item.monitor_step >= 0));
    } catch (error) {
      console.error(error);
      toast.error('Bestellmonitor konnte nicht geladen werden');
    } finally {
      if (!silent) setLoading(false);
    }
  }, [authLoading, isAuthenticated, profileId]);

  useEffect(() => {
    if (!authLoading && isAuthenticated) {
      loadMonitorData();
    }
  }, [authLoading, isAuthenticated, loadMonitorData]);

  const applyUndoAction = useCallback(async () => {
    if (!undoAction?.onUndo) return;

    const undoCallback = undoAction.onUndo;
    clearUndoAction();

    try {
      await undoCallback();
      toast.success('Letzte Aktion rückgängig gemacht');
    } catch (error) {
      console.error(error);
      toast.error('Rückgängig konnte nicht ausgeführt werden');
      loadMonitorData({ silent: true });
    }
  }, [clearUndoAction, loadMonitorData, undoAction]);

  const scheduleReload = useCallback(() => {
    if (reloadTimeoutRef.current) {
      window.clearTimeout(reloadTimeoutRef.current);
    }

    reloadTimeoutRef.current = window.setTimeout(() => {
      loadMonitorData({ silent: true });
    }, 180);
  }, [loadMonitorData]);

  useEffect(() => {
    if (!profile) return undefined;

    const printGroups = profile.print_groups || [];
    channelRef.current = subscribeToMonitor(profile.company_id || null, printGroups, {
      onInsert: (newItem) => {
        if (newItem.monitor_step < 0) return;

        const ticketKey = newItem.order_id || newItem.order_number || newItem.id;
        const orderAlreadyVisible = itemsRef.current.some(
          (item) => (item.order_id || item.order_number || item.id) === ticketKey,
        );

        if (!orderAlreadyVisible) {
          if (soundEnabledRef.current) playKitchenAlertTone();
          if (voiceEnabledRef.current) speakTicketAnnouncement(buildTicketFromItems([newItem]));
          if (navigator.vibrate) navigator.vibrate([80, 40, 80]);
        }

        scheduleReload();
      },
      onUpdate: () => {
        scheduleReload();
      },
      onDelete: () => {
        scheduleReload();
      },
    });

    return () => {
      if (reloadTimeoutRef.current) {
        window.clearTimeout(reloadTimeoutRef.current);
      }
      if (undoTimeoutRef.current) {
        window.clearTimeout(undoTimeoutRef.current);
      }
      if (channelRef.current) {
        supabase.removeChannel(channelRef.current);
      }
    };
  }, [profile, scheduleReload]);

  const groupedTickets = useMemo(() => {
    const grouped = new Map();

    items.forEach((item) => {
      const key = item.order_id || item.order_number || item.id;
      const current = grouped.get(key) || {
        key,
        orderId: item.order_id,
        orderNumber: item.order_number,
        tableName: item.table_name,
        roomName: item.room_name,
        cashierName: item.cashier_name,
        customerName: item.customer_name,
        fulfillmentType: item.fulfillment_type,
        orderSource: item.order_source,
        requestedFulfillmentAt: item.requested_fulfillment_at,
        confirmedFulfillmentAt: item.confirmed_fulfillment_at,
        created_at: item.created_at,
        orderCreatedAt: item.order_created_at || item.created_at,
        items: [],
        stepValues: [],
        stationGroups: [],
      };

      if (!current.created_at || new Date(item.created_at) < new Date(current.created_at)) {
        current.created_at = item.created_at;
      }

      if (!current.orderCreatedAt || new Date(item.order_created_at || item.created_at) < new Date(current.orderCreatedAt)) {
        current.orderCreatedAt = item.order_created_at || item.created_at;
      }

      current.tableName = current.tableName || item.table_name;
      current.roomName = current.roomName || item.room_name;
      current.cashierName = current.cashierName || item.cashier_name;
      current.customerName = current.customerName || item.customer_name;
      current.fulfillmentType = current.fulfillmentType || item.fulfillment_type;
      current.orderSource = current.orderSource || item.order_source;
      current.requestedFulfillmentAt = current.requestedFulfillmentAt || item.requested_fulfillment_at;
      current.confirmedFulfillmentAt = current.confirmedFulfillmentAt || item.confirmed_fulfillment_at;
      current.items.push(item);
      current.stepValues.push(item.monitor_step);

      if (item.print_group && !current.stationGroups.includes(item.print_group)) {
        current.stationGroups.push(item.print_group);
      }

      grouped.set(key, current);
    });

    return Array.from(grouped.values()).map((ticket) => {
      const uniqueSteps = Array.from(new Set(ticket.stepValues.filter((step) => step >= 0)));
      return {
        ...ticket,
        activeStep: uniqueSteps.length ? Math.min(...uniqueSteps) : 0,
      };
    });
  }, [items]);

  const warnMinutes = profile?.warn_minutes ?? 8;
  const alertMinutes = profile?.alert_minutes ?? 15;
  const orderTimes = profile?.order_times || {
    pickupLeadMinutes: 20,
    deliveryLeadMinutes: 35,
  };
  const showOrderSource = profile?.show_order_source ?? true;
  const showFulfillmentType = profile?.show_fulfillment_type ?? true;
  const monitorSettingsUnlocked = monitorSettingsUnlockedUntil > nowTs;

  const stationOptions = useMemo(() => {
    const definedStations = profile?.print_groups || [];
    const currentStations = groupedTickets.flatMap((ticket) => ticket.stationGroups || []);
    return ['all', ...Array.from(new Set([...definedStations, ...currentStations]))];
  }, [groupedTickets, profile]);

  useEffect(() => {
    if (selectedStation === 'all') return;
    if (!stationOptions.includes(selectedStation)) {
      setSelectedStation('all');
    }
  }, [selectedStation, stationOptions]);

  const baseTickets = useMemo(() => {
    return groupedTickets.filter((ticket) => {
      const ageMinutes = minutesSince(getArrivalTimestamp(ticket), nowTs);

      if (selectedStation !== 'all' && !ticket.stationGroups.includes(selectedStation)) {
        return false;
      }

      if (showOnlyAlerts && ageMinutes < warnMinutes) {
        return false;
      }

      return true;
    });
  }, [groupedTickets, nowTs, selectedStation, showOnlyAlerts, warnMinutes]);

  const stageCounts = useMemo(() => ({
    open: baseTickets.filter((ticket) => ticket.activeStep < 2).length,
    0: baseTickets.filter((ticket) => ticket.activeStep === 0).length,
    1: baseTickets.filter((ticket) => ticket.activeStep === 1).length,
    2: baseTickets.filter((ticket) => ticket.activeStep === 2).length,
    all: baseTickets.length,
  }), [baseTickets]);

  const visibleTickets = useMemo(() => {
    const filtered = baseTickets.filter((ticket) => {
      if (selectedStage === 'open') return ticket.activeStep < 2;
      if (selectedStage === 'all') return true;
      return ticket.activeStep === Number(selectedStage);
    });

    return filtered.slice().sort((left, right) => (
      new Date(left.orderCreatedAt || left.created_at) - new Date(right.orderCreatedAt || right.created_at)
    ));
  }, [baseTickets, selectedStage]);

  const ticketColumnCount = useMemo(() => {
    const measuredWidth = Math.max(boardWidth, TICKET_MIN_WIDTH);
    const availableColumns = Math.floor(measuredWidth / TICKET_MIN_WIDTH);
    return Math.max(1, Math.min(ticketColumnLimit, availableColumns || 1));
  }, [boardWidth, ticketColumnLimit]);

  const ticketColumns = useMemo(() => {
    const columns = Array.from({ length: ticketColumnCount }, () => []);

    visibleTickets.forEach((ticket, ticketIndex) => {
      columns[ticketIndex % ticketColumnCount].push({ ticket, ticketIndex });
    });

    return columns;
  }, [ticketColumnCount, visibleTickets]);

  const handleAdvanceTicket = useCallback(async (ticket, nextStep) => {
    const targetIds = ticket.items.map((item) => item.id);
    const previousItems = ticket.items.map((item) => ({
      id: item.id,
      monitor_step: item.monitor_step,
    }));

    setItems((previous) => previous.map((item) => (
      targetIds.includes(item.id)
        ? { ...item, monitor_step: nextStep }
        : item
    )));

    try {
      await Promise.all(targetIds.map((itemId) => api.updateMonitorItemStep(itemId, nextStep)));
      queueUndoAction({
        message: `${getTicketTitle(ticket)} auf ${STAGE_META[previousItems[0]?.monitor_step ?? 0]?.label || 'NEU'} zurücksetzen`,
        onUndo: async () => {
          setItems((previous) => previous.map((item) => {
            const original = previousItems.find((candidate) => candidate.id === item.id);
            return original ? { ...item, monitor_step: original.monitor_step } : item;
          }));
          await Promise.all(previousItems.map((item) => api.updateMonitorItemStep(item.id, item.monitor_step)));
        },
      });
    } catch (error) {
      console.error(error);
      toast.error('Status konnte nicht aktualisiert werden');
      loadMonitorData({ silent: true });
    }
  }, [loadMonitorData, queueUndoAction]);

  const handleDoneTicket = useCallback(async (ticket) => {
    const targetIds = ticket.items.map((item) => item.id);
    const previousItems = ticket.items.map((item) => ({
      id: item.id,
      monitor_step: item.monitor_step,
      status: item.status || 'pending',
      notes: item.notes || '',
      readyQuantity: getItemReadyQuantity(item),
    }));
    setItems((previous) => previous.filter((item) => !targetIds.includes(item.id)));

    try {
      await Promise.all(targetIds.map((itemId) => api.markMonitorItemDone(itemId)));
      queueUndoAction({
        message: `${getTicketTitle(ticket)} wiederherstellen`,
        onUndo: async () => {
          setItems((previous) => [
            ...previous,
            ...ticket.items,
          ]);
          await Promise.all(previousItems.map(async (item) => {
            await api.updateMonitorItemStep(item.id, item.monitor_step);
            await api.updateMonitorItemStatus(item.id, item.status, item.readyQuantity);
          }));
          loadMonitorData({ silent: true });
        },
      });
    } catch (error) {
      console.error(error);
      toast.error('Ticket konnte nicht archiviert werden');
      loadMonitorData({ silent: true });
    }
  }, [loadMonitorData, queueUndoAction]);

  const handlePrintTicket = useCallback(async (ticket) => {
    if (!ticket.orderId || !ticket.items.length) {
      toast.error('Dieser Bon kann nicht gedruckt werden');
      return;
    }

    setPrintingTicketIds((current) => [...current, ticket.key]);
    try {
      const result = await api.printMonitorTicket(ticket.orderId, ticket.items.map((item) => item.id));
      if (result.printed > 0) {
        const printerNames = (result.jobs || [])
          .map((job) => job.printer_name)
          .filter(Boolean);
        toast.success(printerNames.length ? `Bon an ${printerNames.join(', ')} gesendet` : 'Produktionsbon gesendet');
      } else {
        toast.error(result.errors?.[0]?.message || result.message || 'Für dieses Ticket ist kein Drucker eingerichtet');
      }
    } catch (error) {
      console.error(error);
      toast.error(error?.response?.data?.detail || 'Produktionsbon konnte nicht gedruckt werden');
    } finally {
      setPrintingTicketIds((current) => current.filter((ticketKey) => ticketKey !== ticket.key));
    }
  }, []);

  const handleMarkItemPortionReady = useCallback(async (groupItems) => {
    const targetItem = groupItems
      .slice()
      .sort((left, right) => new Date(left.created_at || 0) - new Date(right.created_at || 0))
      .find((item) => getItemReadyQuantity(item) < Math.max(1, Number(item.quantity) || 1));

    if (!targetItem) return;

    const previousReadyQuantity = getItemReadyQuantity(targetItem);
    const nextReadyQuantity = previousReadyQuantity + 1;
    const previousSnapshot = {
      id: targetItem.id,
      status: targetItem.status || 'pending',
      notes: targetItem.notes || '',
      readyQuantity: previousReadyQuantity,
    };
    const nextItem = buildItemWithReadyQuantity(targetItem, nextReadyQuantity);

    setItems((previous) => previous.map((candidate) => (
      candidate.id === targetItem.id
        ? nextItem
        : candidate
    )));

    try {
      await api.updateMonitorItemStatus(targetItem.id, nextItem.status, nextReadyQuantity);
      queueUndoAction({
        message: `${targetItem.product_name || 'Artikel'} Portion zurücksetzen`,
        onUndo: async () => {
          const restoredItem = {
            ...targetItem,
            status: previousSnapshot.status,
            notes: previousSnapshot.notes,
          };
          setItems((previous) => previous.map((candidate) => (
            candidate.id === previousSnapshot.id
              ? restoredItem
              : candidate
          )));
          await api.updateMonitorItemStatus(
            previousSnapshot.id,
            previousSnapshot.status,
            previousSnapshot.readyQuantity,
          );
        },
      });
    } catch (error) {
      console.error(error);
      toast.error('Artikelstatus konnte nicht aktualisiert werden');
      loadMonitorData({ silent: true });
    }
  }, [loadMonitorData, queueUndoAction]);

  const handleUnlockMonitorSettings = useCallback(async () => {
    if (!monitorSettingsPin.trim()) {
      toast.error('Bitte Manager-PIN eingeben');
      return;
    }

    setUnlockingMonitorSettings(true);
    try {
      const result = await api.unlockMonitorSettings(profileId, monitorSettingsPin);
      setMonitorSettingsUnlockedUntil(new Date(result.unlocked_until).getTime());
      setMonitorSettingsPin('');
      toast.success(`Einstellungen für ${result.unlock_minutes} Minuten geöffnet`);
    } catch (error) {
      toast.error(error?.response?.data?.detail || 'PIN konnte nicht geprüft werden');
    } finally {
      setUnlockingMonitorSettings(false);
    }
  }, [monitorSettingsPin, profileId]);

  if (authLoading || loading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-[#f3f1ee]">
        <div className="h-10 w-10 animate-spin rounded-full border-4 border-[#ff3b14] border-t-transparent" />
      </div>
    );
  }

  if (!isAuthenticated) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-[#f3f1ee] p-6">
        <div className="rounded-2xl border border-slate-200 bg-white px-8 py-7 text-center shadow-sm">
          <p className="text-xl font-bold text-slate-900">Bestellmonitor gesperrt</p>
          <p className="mt-2 text-sm text-slate-500">
            Bitte zuerst im FiscalPOS anmelden.
          </p>
        </div>
      </div>
    );
  }

  if (!profile) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-[#f3f1ee] p-6">
        <div className="rounded-2xl border border-slate-200 bg-white px-8 py-7 text-center shadow-sm">
          <p className="text-xl font-bold text-slate-900">Monitor-Profil nicht gefunden</p>
          <p className="mt-2 text-sm text-slate-500">
            Bitte pruefe die Monitor-Konfiguration im Manager.
          </p>
        </div>
      </div>
    );
  }

  const currentTimeLabel = new Date(nowTs).toLocaleTimeString('de-AT', {
    hour: '2-digit',
    minute: '2-digit',
  });

  const currentDateLabel = new Date(nowTs).toLocaleDateString('de-AT', {
    weekday: 'short',
    day: '2-digit',
    month: '2-digit',
  });

  const stageFilters = [
    { value: 'open', label: 'OFFEN', count: stageCounts.open, className: 'border-[#ff3b14] bg-[#ff3b14] text-white' },
    { value: '0', label: 'NEU', count: stageCounts[0], className: 'border-sky-500 bg-sky-500/20 text-sky-100' },
    { value: '1', label: 'IN ARBEIT', count: stageCounts[1], className: 'border-orange-400 bg-orange-500/20 text-orange-100' },
    { value: '2', label: 'FERTIG', count: stageCounts[2], className: 'border-emerald-400 bg-emerald-500/20 text-emerald-100' },
    { value: 'all', label: 'ALLE', count: stageCounts.all, className: 'border-slate-400 bg-slate-700 text-white' },
  ];

  return (
    <div className="flex min-h-screen flex-col bg-[#0b1220] text-slate-100">
      <header className="border-b border-slate-800 bg-[#111827] shadow-[0_14px_36px_rgba(2,6,23,0.45)]">
        <div className="flex flex-wrap items-center justify-between gap-4 px-4 py-4 xl:flex-nowrap">
          <div className="flex min-w-0 flex-1 flex-wrap items-center gap-2.5">
            <span className="mr-1 text-[10px] font-black uppercase tracking-[0.18em] text-slate-500">
              Station
            </span>
            {stationOptions.map((station) => (
              <button
                key={station}
                type="button"
                onClick={() => setSelectedStation(station)}
                className={`rounded-full border px-4 py-2 text-[11px] font-black uppercase tracking-[0.08em] transition-colors ${
                  selectedStation === station
                    ? 'border-[#ff3b14] bg-[#ff3b14] text-white shadow-[0_0_0_1px_rgba(255,59,20,0.2)]'
                    : 'border-slate-700 bg-slate-900 text-slate-200 hover:border-slate-500 hover:text-white'
                }`}
              >
                {(station === 'all' ? 'ALLE' : station).toUpperCase()}
              </button>
            ))}

            <div className="mx-1 hidden h-8 w-px bg-slate-700 xl:block" />

            <span className="mr-1 text-[10px] font-black uppercase tracking-[0.18em] text-slate-500">
              Status
            </span>
            {stageFilters.map((stage) => (
              <button
                key={stage.value}
                type="button"
                onClick={() => setSelectedStage(stage.value)}
                className={`rounded-full border px-4 py-2 text-[11px] font-black uppercase tracking-[0.08em] transition-colors ${
                  selectedStage === stage.value
                    ? stage.className
                    : 'border-slate-700 bg-slate-900 text-slate-300 hover:border-slate-500 hover:text-white'
                }`}
              >
                {stage.label} ({stage.count})
              </button>
            ))}
          </div>

          <div className="relative flex flex-wrap items-center justify-end gap-2">
            <button
              type="button"
              onClick={() => setShowOnlyAlerts((value) => !value)}
              className={`inline-flex items-center gap-1.5 rounded-full border px-3 py-2 text-[11px] font-black uppercase tracking-[0.08em] ${
                showOnlyAlerts
                  ? 'border-amber-400 bg-amber-500/20 text-amber-100'
                  : 'border-slate-700 bg-slate-900 text-slate-300'
              }`}
            >
              <AlertCircle className="h-3.5 w-3.5" />
              Alarm
            </button>

            <button
              type="button"
              onClick={() => setSoundEnabled((value) => !value)}
              className={`inline-flex items-center gap-1.5 rounded-full border px-3 py-2 text-[11px] font-black uppercase tracking-[0.08em] ${
                soundEnabled
                  ? 'border-slate-600 bg-slate-800 text-white'
                  : 'border-slate-700 bg-slate-900 text-slate-400'
              }`}
            >
              {soundEnabled ? <Bell className="h-3.5 w-3.5" /> : <BellOff className="h-3.5 w-3.5" />}
              Sound
            </button>

            <button
              type="button"
              onClick={() => setVoiceEnabled((value) => !value)}
              className={`inline-flex items-center gap-1.5 rounded-full border px-3 py-2 text-[11px] font-black uppercase tracking-[0.08em] ${
                voiceEnabled
                  ? 'border-lime-400 bg-lime-500/20 text-lime-100'
                  : 'border-slate-700 bg-slate-900 text-slate-300'
              }`}
            >
              {voiceEnabled ? <Volume2 className="h-3.5 w-3.5" /> : <VolumeX className="h-3.5 w-3.5" />}
              Stimme
            </button>

            <button
              type="button"
              onClick={() => setShowSummary((value) => !value)}
              className="inline-flex items-center gap-2 rounded-full border border-slate-700 bg-slate-900 px-3 py-2 text-[11px] font-black uppercase tracking-[0.08em] text-[#ff8c76] transition-colors hover:border-slate-500 hover:text-[#ffb7a9]"
            >
              {showSummary ? <PanelRightClose className="h-3.5 w-3.5" /> : <PanelRightOpen className="h-3.5 w-3.5" />}
              {showSummary ? 'Uebersicht ausblenden' : 'Uebersicht einblenden'}
            </button>

            <button
              type="button"
              onClick={() => setShowMonitorSettings((value) => !value)}
              title="Monitor-Einstellungen"
              aria-label="Monitor-Einstellungen"
              className={`inline-flex h-9 w-9 items-center justify-center rounded-full border transition-colors ${
                showMonitorSettings
                  ? 'border-[#ff8c76] bg-[#ff3b14] text-white'
                  : 'border-slate-700 bg-slate-900 text-slate-300 hover:border-slate-500 hover:text-white'
              }`}
            >
              <Settings2 className="h-4 w-4" />
            </button>

            {showMonitorSettings ? (
              <div className="absolute right-0 top-11 z-30 w-72 border border-slate-700 bg-[#111827] p-3 shadow-[0_16px_34px_rgba(2,6,23,0.5)]">
                {!monitorSettingsUnlocked ? (
                  <div>
                    <div className="flex items-center gap-2 text-slate-100">
                      <ShieldCheck className="h-4 w-4 text-[#ff8c76]" />
                      <p className="text-xs font-black uppercase tracking-[0.1em]">Manager-Bereich</p>
                    </div>
                    <p className="mt-2 text-xs leading-relaxed text-slate-400">
                      Mit Manager-PIN werden Monitor-Einstellungen und Live-Küchenstatus kurz freigeschaltet.
                    </p>
                    <input
                      type="password"
                      inputMode="numeric"
                      value={monitorSettingsPin}
                      onChange={(event) => setMonitorSettingsPin(event.target.value.replace(/\D/g, '').slice(0, 6))}
                      onKeyDown={(event) => {
                        if (event.key === 'Enter') handleUnlockMonitorSettings();
                      }}
                      placeholder="Manager-PIN"
                      className="mt-3 w-full border border-slate-600 bg-slate-950 px-3 py-2 text-center text-sm tracking-[0.35em] text-white outline-none placeholder:tracking-normal placeholder:text-slate-500 focus:border-[#ff8c76]"
                    />
                    <button
                      type="button"
                      onClick={handleUnlockMonitorSettings}
                      disabled={unlockingMonitorSettings}
                      className="mt-2 w-full bg-[#ff3b14] px-3 py-2 text-xs font-black uppercase tracking-[0.08em] text-white transition-colors hover:bg-[#e13612] disabled:cursor-wait disabled:opacity-60"
                    >
                      {unlockingMonitorSettings ? 'Prüfe...' : 'Freischalten'}
                    </button>
                  </div>
                ) : (
                  <div>
                    <div className="flex items-center gap-2 text-slate-100">
                      <ShieldCheck className="h-4 w-4 text-emerald-400" />
                      <p className="text-xs font-black uppercase tracking-[0.1em]">Manager-Bereich offen</p>
                    </div>
                    <p className="mt-1 text-xs text-slate-400">Bon-Spalten auf diesem Monitor</p>
                    <div className="mt-3 grid grid-cols-5 gap-1.5">
                      {[2, 3, 4, 5, 6].map((count) => (
                        <button
                          key={count}
                          type="button"
                          onClick={() => setTicketColumnLimit(count)}
                          className={`border px-2 py-2 text-xs font-black transition-colors ${
                            ticketColumnLimit === count
                              ? 'border-[#ff8c76] bg-[#ff3b14] text-white'
                              : 'border-slate-700 bg-slate-900 text-slate-300 hover:border-slate-500 hover:text-white'
                          }`}
                        >
                          {count}
                        </button>
                      ))}
                    </div>
                    <div className="mt-4 border-t border-slate-800 pt-3">
                      <p className="text-[10px] font-black uppercase tracking-[0.14em] text-slate-400">Live-Küchenstatus</p>
                      <div className="mt-2 grid grid-cols-3 gap-1.5 text-center">
                        <div className="bg-slate-900 px-2 py-2"><p className="text-lg font-black text-white">{stageCounts.open}</p><p className="text-[9px] font-bold uppercase text-slate-400">offen</p></div>
                        <div className="bg-slate-900 px-2 py-2"><p className="text-lg font-black text-amber-300">{baseTickets.filter((ticket) => minutesSince(getArrivalTimestamp(ticket), nowTs) >= warnMinutes).length}</p><p className="text-[9px] font-bold uppercase text-slate-400">warnung</p></div>
                        <div className="bg-slate-900 px-2 py-2"><p className="text-lg font-black text-[#ff8c76]">{baseTickets.filter((ticket) => minutesSince(getArrivalTimestamp(ticket), nowTs) >= alertMinutes).length}</p><p className="text-[9px] font-bold uppercase text-slate-400">kritisch</p></div>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            ) : null}

            <div className="inline-flex items-center gap-2 rounded-full border border-slate-700 bg-slate-900 px-3 py-2 text-[11px] font-semibold text-slate-300">
              <span className="font-black uppercase tracking-[0.08em] text-slate-100">Wetter</span>
              <span className="text-slate-400">
              {weather.status === 'ready' && weather.temperature !== null
                ? `${weather.temperature}°C ${weather.label}`
                : weather.label}
              </span>
              {weather.location ? (
                <span className="text-slate-500">{weather.location}</span>
              ) : null}
            </div>

            <div className="inline-flex items-center gap-2 rounded-full border border-slate-700 bg-slate-900 px-3 py-2 text-[11px] font-semibold text-slate-300">
              <span className="text-slate-500">{currentDateLabel}</span>
              <span className="text-[18px] font-black tracking-[0.06em] text-white tabular-nums">
                {currentTimeLabel}
              </span>
            </div>
          </div>
        </div>
      </header>

      {undoAction ? (
        <section className="border-b border-amber-400/30 bg-amber-500/10 px-4 py-3">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <p className="text-sm font-semibold text-amber-100">
              {undoAction.message}
            </p>
            <button
              type="button"
              onClick={applyUndoAction}
              className="inline-flex items-center gap-2 rounded-full border border-amber-300 bg-amber-50 px-3 py-2 text-sm font-bold text-amber-900 transition-colors hover:bg-amber-100"
            >
              <RotateCcw className="h-4 w-4" />
              Rueckgaengig
            </button>
          </div>
        </section>
      ) : null}

      <main className={`grid min-h-0 flex-1 ${showSummary ? 'xl:grid-cols-[minmax(0,1fr)_240px]' : 'grid-cols-1'}`}>
        <section className="min-h-0 overflow-auto bg-transparent">
          {visibleTickets.length ? (
            <div ref={boardRef} className="w-full">
              <div
                className="grid w-full items-start"
                style={{ gridTemplateColumns: `repeat(${ticketColumnCount}, minmax(0, 1fr))` }}
              >
                {ticketColumns.map((column, columnIndex) => (
                  <div key={`ticket-column-${columnIndex}`} className="flex min-w-0 flex-col">
                    {column.map(({ ticket, ticketIndex }) => (
                      <MonitorTicket
                        key={ticket.key}
                        ticket={ticket}
                        ticketIndex={ticketIndex}
                        nowTs={nowTs}
                        warnMinutes={warnMinutes}
                        alertMinutes={alertMinutes}
                        orderTimes={orderTimes}
                        showOrderSource={showOrderSource}
                        showFulfillmentType={showFulfillmentType}
                        isPrinting={printingTicketIds.includes(ticket.key)}
                        onAdvanceTicket={handleAdvanceTicket}
                        onDoneTicket={handleDoneTicket}
                        onMarkItemGroupPortionReady={handleMarkItemPortionReady}
                        onPrintTicket={handlePrintTicket}
                      />
                    ))}
                  </div>
                ))}
              </div>
            </div>
          ) : (
            <div className="mx-4 flex min-h-[60vh] flex-col items-center justify-center rounded-[28px] border border-dashed border-slate-600 bg-slate-900/60 text-center shadow-[inset_0_1px_0_rgba(255,255,255,0.04)]">
              <CheckCircle2 className="h-14 w-14 text-emerald-500" />
              <h2 className="mt-4 text-2xl font-black text-white">Keine sichtbaren Bestellungen</h2>
              <p className="mt-2 max-w-md text-sm text-slate-400">
                Im aktuellen Stations- und Statusfilter liegen gerade keine offenen Tickets.
              </p>
            </div>
          )}
        </section>

        {showSummary ? (
          <OpenItemsRail
            tickets={visibleTickets}
            onMarkOpenItemReady={handleMarkItemPortionReady}
          />
        ) : null}
      </main>
    </div>
  );
}
